import React, { useState, useRef } from 'react';
import { OcrInvoiceItem } from '../types';
import { api } from '../api';
import { tg } from '../telegram';
import {
  Camera,
  Plus,
  Trash2,
  RefreshCw,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Image as ImageIcon,
  PenLine,
  AlertTriangle,
} from 'lucide-react';
import { CameraCapture } from '../components/CameraCapture';
import { ConfirmView, ConfirmItem } from '../components/ui/ConfirmView';
import { SuccessView } from '../components/ui/SuccessView';
import { ItemDropdown } from '../components/ui/ItemDropdown';
import { AddInwardItemModal } from '../components/ui/AddInwardItemModal';
import { useSubmitAction } from '../hooks/useSubmitAction';
import { useMasterData } from '../hooks/useMasterData';
import { formatQty } from '../utils/format';
import toast from 'react-hot-toast';

interface Props {
  onBack: () => void;
  onSuccess: () => void;
}

export const GoodsInwardScreen: React.FC<Props> = ({ onBack, onSuccess }) => {
  const { inwardItems, conversions, convertInwardItem, refresh, loading } = useMasterData();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [items, setItems] = useState<OcrInvoiceItem[]>([]);
  const [note, setNote] = useState('');
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Click 1: Upload or take photo -> Trigger OCR
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    tg.haptic.impact('medium');
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      await processImageOcr(base64, file.type || 'image/jpeg');
    };
    reader.readAsDataURL(file);
  };

  const handleCameraCapture = async (base64Image: string) => {
    await processImageOcr(base64Image, 'image/jpeg');
  };

  const processImageOcr = async (base64: string, mimeType: string) => {
    setImagePreview(base64);
    setOcrLoading(true);
    const toastId = toast.loading('AI Gemini đang phân tích hóa đơn...');

    try {
      const ocrResult = await api.scanGoodsInwardOcr(base64, mimeType);
      const convertedItems = (ocrResult.items || []).map((it) => {
        const conv = convertInwardItem(it.name, it.package_unit, it.quantity);
        return {
          ...it,
          standard_quantity: conv.standard_quantity,
          standard_unit: conv.standard_unit,
          conversion_applied: conv.converted,
        };
      });
      setItems(convertedItems);
      if (ocrResult.note) setNote(ocrResult.note);
      setStep(2); // Auto proceed to Step 2 (Verify)
      toast.success(`Đã nhận diện ${convertedItems.length} món từ hóa đơn`, { id: toastId });
      tg.haptic.success();
    } catch (err: any) {
      toast.error(`Không thể bóc tách ảnh: ${err.message}. Bạn có thể nhập tay.`, { id: toastId });
      setStep(2);
    } finally {
      setOcrLoading(false);
    }
  };

  const handleItemSelect = (index: number, selectedNameOrKey: string, _unit?: string, option?: any) => {
    const matchedInward = option || inwardItems.find(
      (it) => it.key === selectedNameOrKey || it.name.toLowerCase() === selectedNameOrKey.trim().toLowerCase()
    );

    const itemName = matchedInward ? matchedInward.name : selectedNameOrKey.trim();
    const pkgUnit = matchedInward ? matchedInward.package_unit : (items[index]?.package_unit || 'đơn vị');
    const currentQty = items[index]?.quantity || 1;
    const conv = convertInwardItem(itemName, pkgUnit, currentQty);

    setItems((prev) =>
      prev.map((it, i) =>
        i === index
          ? {
              ...it,
              name: itemName,
              package_unit: pkgUnit,
              standard_unit: conv.standard_unit,
              standard_quantity: conv.standard_quantity,
              conversion_applied: conv.converted,
            }
          : it
      )
    );
  };

  const setItemQty = (index: number, val: number) => {
    const newQty = Math.max(0, val);
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const conv = convertInwardItem(item.name, item.package_unit, newQty);
        return {
          ...item,
          quantity: newQty,
          standard_quantity: conv.standard_quantity,
          standard_unit: conv.standard_unit,
          conversion_applied: conv.converted,
        };
      })
    );
  };

  const removeItem = (index: number) => {
    tg.haptic.impact('light');
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const addNewItem = () => {
    tg.haptic.impact('light');
    setIsAddModalOpen(true);
  };

  const handleAddItem = (newItem: OcrInvoiceItem) => {
    setItems((prev) => [...prev, newItem]);
    toast.success(`Đã thêm "${newItem.name}" (${newItem.quantity} ${newItem.package_unit}) vào cuối phiếu`);
  };

  // Submit action hook
  const { isSubmitting, isDisabled, execute: submitInward } = useSubmitAction(
    async () => {
      const validItems = items.filter((item) => item.name.trim() && item.quantity > 0);
      await api.confirmGoodsInward(
        validItems.map((item) => ({
          ingredient_name: item.name.trim(),
          package_unit: item.package_unit.trim(),
          quantity: item.quantity,
          amount: item.amount,
        })),
        note
      );
    },
    {
      successMessage: 'Nhập hàng Kho Tổng thành công!',
      onSuccess: () => {
        setStep(4);
      },
    }
  );

  const goToConfirm = () => {
    const validItems = items.filter((item) => item.name.trim() && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Vui lòng nhập ít nhất 1 mặt hàng hợp lệ.');
      return;
    }

    // Chặn đứng hoàn toàn nếu có món chưa có trong 04_Quy_doi (KHÔNG FALLBACK NGẦM!)
    const invalidItem = validItems.find((it) => {
      const conv = convertInwardItem(it.name, it.package_unit, it.quantity);
      return !conv.converted;
    });

    if (invalidItem) {
      tg.haptic.error();
      toast.error(
        `Món "${invalidItem.name}" (${invalidItem.package_unit}) chưa có trong bảng 04_Quy_doi. Vui lòng khai báo vào Google Sheet rồi sync về!`,
        { duration: 5000 }
      );
      return;
    }

    tg.haptic.impact('light');
    setStep(3);
  };

  // 0. BLOCKER ERROR VIEW: Nếu Database chưa có dữ liệu 04_Quy_doi -> Chặn đứng hoàn toàn!
  if (!loading && conversions.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={onBack} className="stepper-btn" title="Quay lại">
            <ArrowLeft size={18} />
          </button>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: 16, fontWeight: 800 }}>Luồng 1: Nhập Hàng Kho Tổng</h2>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Yêu cầu dữ liệu bảng 04_Quy_doi</span>
          </div>
          <div style={{ width: 36 }} />
        </div>

        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: '28px 18px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            border: '2px dashed #EF4444',
            backgroundColor: 'rgba(239, 68, 68, 0.04)',
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#EF4444',
            }}
          >
            <AlertTriangle size={36} />
          </div>

          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: '#DC2626' }}>
              Chưa Có Dữ Liệu Bảng 04_Quy_doi!
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>
              Kho Tổng <b>chỉ nhập Nguyên liệu thô</b> theo các quy cách đã được định nghĩa trong Google Sheet <b>04_Quy_doi</b>. Cơ sở dữ liệu hiện đang có 0 dòng quy đổi. Hệ thống <b>tuyệt đối không fallback ngầm</b> để đảm bảo tính toàn vẹn tồn kho.
            </p>
          </div>

          <div
            style={{
              width: '100%',
              backgroundColor: 'var(--bg-surface-subtle)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              padding: '12px 14px',
              textAlign: 'left',
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--text-primary)' }}>
              📋 Hướng dẫn xử lý:
            </div>
            <ol style={{ paddingLeft: 18, margin: 0, color: 'var(--text-secondary)' }}>
              <li>Mở Google Sheet quản lý quán bar.</li>
              <li>Kiểm tra sheet tab <b>04_Quy_doi</b> và nhập quy cách đóng gói (VD: Trà đào Cozy | Thùng | 24 | túi).</li>
              <li>Trên thanh menu Google Sheet, chọn: <b>Quản Lý Quầy Bar ➔ [Đồng bộ ngay lên Cloudflare (Sync Now)]</b>.</li>
              <li>Sau khi đồng bộ thành công, quay lại đây bấm nút <b>Đồng bộ lại dữ liệu</b> bên dưới.</li>
            </ol>
          </div>

          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            <button
              onClick={async () => {
                tg.haptic.impact('medium');
                const tid = toast.loading('Đang tải lại dữ liệu từ Cloudflare...');
                await refresh();
                toast.success('Đã làm mới dữ liệu!', { id: tid });
              }}
              className="btn btn-primary btn-full"
              style={{ minHeight: 48, fontSize: 14 }}
            >
              <RefreshCw size={18} /> Đồng Bộ Lại Dữ Liệu Ngay
            </button>
            <button
              onClick={onBack}
              className="btn btn-secondary btn-full"
              style={{ minHeight: 42, fontSize: 13 }}
            >
              ⬅️ Quay Về Trang Chủ
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 1. Success Step
  if (step === 4) {
    return (
      <SuccessView
        title="Nhập Kho Thành Công!"
        message="Số lượng đã được quy đổi chuẩn theo 04_Quy_doi và cộng trực tiếp vào Kho Tổng. Thông báo chi tiết đã gửi qua Telegram."
        onDone={onSuccess}
      />
    );
  }

  // 2. Confirm Step
  if (step === 3) {
    const confirmItems: ConfirmItem[] = items
      .filter((item) => item.name.trim() && item.quantity > 0)
      .map((it) => {
        const conv = convertInwardItem(it.name, it.package_unit, it.quantity);
        return {
          label: it.name,
          value: formatQty(it.quantity, it.package_unit),
          sub: conv.converted
            ? `Quy đổi chuẩn (04_Quy_doi): +${formatQty(conv.standard_quantity, conv.standard_unit)}`
            : `Đơn vị chuẩn: +${formatQty(it.quantity, conv.standard_unit)}`,
          badge: 'Kho Tổng (Thô)',
        };
      });

    return (
      <ConfirmView
        title="Xác Nhận Nhập Hàng Kho Tổng"
        description="Kiểm tra kỹ danh sách hàng hóa và quy đổi chuẩn trước khi cộng vào Kho Tổng:"
        items={confirmItems}
        note={note}
        onConfirm={submitInward}
        onBack={() => setStep(2)}
        isSubmitting={isSubmitting}
        isDisabled={isDisabled}
        confirmText="Xác Nhận Lưu Vào Kho Tổng"
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Step Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={onBack} className="stepper-btn" title="Quay lại">
          <ArrowLeft size={18} />
        </button>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 16, fontWeight: 800 }}>Luồng 1: Nhập Hàng Kho Tổng</h2>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Nhập hàng từ NCC về Kho Tổng</span>
        </div>
        <div style={{ width: 36 }} />
      </div>

      {/* Flow Step Bar */}
      <div className="flow-step-bar">
        <div className={`step-indicator ${step >= 1 ? 'active' : ''}`}>
          <span className="step-num">1</span> Chụp Ảnh
        </div>
        <div style={{ flex: 1, height: 2, background: step >= 2 ? 'var(--primary-accent)' : 'var(--border-color)' }} />
        <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>
          <span className="step-num">2</span> Chạm Sửa
        </div>
        <div style={{ flex: 1, height: 2, background: step >= 3 ? 'var(--primary-accent)' : 'var(--border-color)' }} />
        <div className={`step-indicator ${step >= 3 ? 'active' : ''}`}>
          <span className="step-num">3</span> Xác Nhận
        </div>
      </div>

      {/* Step 1: Upload or Camera Input */}
      {step === 1 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: 16, fontWeight: 800 }}>Chọn ảnh Phiếu Nhập Hàng</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              Hóa đơn mua từ NCC hoặc Phiếu in sẵn điền tay.
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <button
              onClick={() => setIsCameraOpen(true)}
              className="btn btn-primary"
              style={{ minHeight: 70, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', justifyContent: 'center' }}
            >
              <Camera size={22} />
              <span style={{ fontSize: 13 }}>Mở Camera Chụp</span>
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn btn-secondary"
              style={{ minHeight: 70, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', justifyContent: 'center' }}
            >
              <ImageIcon size={22} />
              <span style={{ fontSize: 13 }}>Tải Ảnh Từ Thư Viện</span>
            </button>
          </div>

          {ocrLoading && (
            <div style={{ textAlign: 'center', padding: '20px 16px', background: 'var(--bg-surface-subtle)', borderRadius: 'var(--radius-md)', border: '1.5px solid rgba(245, 158, 11, 0.3)' }}>
              <RefreshCw size={28} className="animate-spin" color="var(--primary-accent)" style={{ margin: '0 auto 8px' }} />
              <div style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: 14 }}>Đang bóc tách phiếu bằng Gemini AI...</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                Đang nhận diện chữ viết tay và quy đổi bao bì sang đơn vị chuẩn
              </div>
              <div className="progress-scanner-bar" />
            </div>
          )}

          <button
            onClick={() => {
              tg.haptic.impact('light');
              setStep(2);
              if (items.length === 0) {
                setIsAddModalOpen(true);
              }
            }}
            className="btn btn-secondary btn-full"
            style={{ fontSize: 13, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <PenLine size={16} /> Nhập liệu thủ công (không dùng ảnh)
          </button>
        </div>
      )}

      {/* Step 2: Verification & Adjustments */}
      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {imagePreview && (
            <div className="card" style={{ padding: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <img
                src={imagePreview}
                alt="Receipt Preview"
                style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
              />
              <div style={{ flex: 1 }}>
                <span className="badge badge-success" style={{ fontSize: 10 }}>
                  <ShieldCheck size={12} /> Gemini OCR Đã Xử Lý
                </span>
                <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>
                  Đã tự động quy đổi bao bì sang đơn vị tiêu chuẩn
                </div>
              </div>
              <button
                onClick={() => setStep(1)}
                className="badge badge-neutral"
                style={{ border: 'none', cursor: 'pointer' }}
              >
                Chụp lại
              </button>
            </div>
          )}

          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="card-header" style={{ marginBottom: 4 }}>
              <span className="card-title">Kiểm Tra & Chỉnh Sửa Số Lượng</span>
              <button onClick={addNewItem} className="badge badge-success" style={{ border: 'none', cursor: 'pointer', padding: '5px 8px' }}>
                <Plus size={12} /> Thêm món
              </button>
            </div>

            {items.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '28px 16px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--bg-surface-subtle)',
                  border: '1.5px dashed var(--border-color)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div style={{ fontSize: 28 }}>📦</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Chưa có món nào trong phiếu nhập
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Bấm nút bên dưới để chọn nguyên liệu và nhập số lượng
                </div>
                <button
                  type="button"
                  onClick={addNewItem}
                  className="btn btn-primary"
                  style={{ minHeight: 38, padding: '6px 16px', fontSize: 13, marginTop: 4 }}
                >
                  <Plus size={15} /> Thêm Món Vào Phiếu
                </button>
              </div>
            ) : (
              <>
                {/* Table Header */}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 64px 48px 30px', gap: 6, padding: '0 11px', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>
                  <div>TÊN NGUYÊN LIỆU THÔ</div>
                  <div style={{ textAlign: 'center' }}>SL NHẬP</div>
                  <div style={{ textAlign: 'center' }}>ĐVT</div>
                  <div></div>
                </div>

                {items.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) 64px 48px 30px',
                      gap: 6,
                      alignItems: 'flex-start',
                      padding: '8px 10px',
                      borderRadius: 'var(--radius-md)',
                      backgroundColor: 'var(--bg-surface-subtle)',
                      border: item.conversion_applied ? '1px solid var(--border-color)' : '1px solid #EF4444',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <ItemDropdown
                        value={item.name}
                        items={inwardItems}
                        placeholder="Chọn nguyên liệu nhập..."
                        onChange={(selectedName, unit, opt) => handleItemSelect(index, selectedName, unit, opt)}
                      />
                      {item.name && (
                        <div style={{ fontSize: 10, marginTop: 4, paddingLeft: 2, lineHeight: 1.3 }}>
                          {item.conversion_applied ? (
                            <span style={{ color: 'var(--color-success)' }}>
                              ↳ Quy đổi chuẩn (04_Quy_doi):{' '}
                              <span className="font-bold">
                                +{formatQty(item.standard_quantity, item.standard_unit)}
                              </span>
                            </span>
                          ) : (
                            <span style={{ color: '#DC2626', fontWeight: 600 }}>
                              ❌ Chưa có trong 04_Quy_doi! Nhập vào Sheet rồi sync về.
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <input
                      type="number"
                      className="input num"
                      placeholder="SL"
                      value={item.quantity === 0 ? '' : item.quantity}
                      onChange={(e) => setItemQty(index, Number(e.target.value))}
                      onBlur={(e) => {
                        if (!e.target.value) setItemQty(index, 0);
                      }}
                      style={{
                        textAlign: 'center',
                        fontWeight: 800,
                        fontSize: 14,
                        padding: '4px 2px',
                        minHeight: 38,
                        height: 38,
                        boxSizing: 'border-box',
                      }}
                    />

                    {/* ĐVT: Nhãn cố định gắn liền với quy cách đã chọn, không bắt chọn thừa thãi */}
                    <div style={{ textAlign: 'center', minWidth: 0, height: 38, display: 'flex', alignItems: 'center' }}>
                      <span
                        className="badge badge-neutral"
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          padding: '4px 2px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          minHeight: 38,
                          height: 38,
                          borderRadius: 'var(--radius-sm)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          boxSizing: 'border-box',
                        }}
                        title={item.package_unit || item.standard_unit || 'đơn vị'}
                      >
                        {item.package_unit || item.standard_unit || 'đơn vị'}
                      </span>
                    </div>

                    {items.length > 1 ? (
                      <button
                        onClick={() => removeItem(index)}
                        className="stepper-btn"
                        style={{
                          width: 30,
                          minHeight: 38,
                          height: 38,
                          color: 'var(--color-danger)',
                          justifySelf: 'center',
                          padding: 0,
                        }}
                        title="Xóa món này"
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : (
                      <div style={{ width: 30, height: 38 }} />
                    )}
                  </div>
                ))}
              </>
            )}

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Ghi chú phiếu nhập:</label>
              <input
                type="text"
                className="input"
                placeholder="VD: NCC Vinamilk, giao đợt 1..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                style={{ marginTop: 4, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <button
            onClick={goToConfirm}
            className="btn btn-primary btn-full"
            style={{ fontSize: 16, padding: '14px 20px', minHeight: 52 }}
          >
            <ArrowRight size={18} /> Xem & Xác Nhận Nhập Kho ({items.length} món)
          </button>
        </div>
      )}

      {/* Camera Capture Modal */}
      <CameraCapture
        isOpen={isCameraOpen}
        onCapture={handleCameraCapture}
        onClose={() => setIsCameraOpen(false)}
      />

      {/* Add Item Modal */}
      <AddInwardItemModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        inwardItems={inwardItems}
        convertInwardItem={convertInwardItem}
        onAdd={handleAddItem}
      />
    </div>
  );
};
